import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';
import { z } from 'zod';

import { Loading } from '#components/loading.js';
import { ProjectName } from '#components/project-name/project-name.js';
import { ResizableLayout } from '#components/resizable-layout/resizable-layout.js';
import { useTRPC } from '#src/trpc.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { WizardPlansForProject } from './wizard-plans-for-project.js';

const searchSchema = z.object({
	/** Filter manufacturerPartNumber by equals. */
	mpn: z.string().optional(),
});

export const Route = createFileRoute('/_authorization-gate/wizard/admin/projects/{-$revisionId}')({
	validateSearch: searchSchema,
	head: () => ({ meta: [{ title: 'Projects • Wizard Admin • Cadstrom' }] }),
	component: WizardAdminProjectsPage,
});

function WizardAdminProjectsPage() {
	const { revisionId } = Route.useParams();
	const { mpn } = Route.useSearch();
	const trpc = useTRPC();

	const {
		data: { projectPreviews },
	} = useSuspenseQuery(trpc.project.listWithRevisions.queryOptions({}));

	const {
		data: { organizations },
	} = useSuspenseQuery(
		trpc.organization.getBatch.queryOptions({
			ids: projectPreviews.map((projectPreview) => projectPreview.ownerId).filter((ownerId) => ownerId !== null),
		}),
	);

	const allOwnerIds = Array.from(
		new Set(projectPreviews.map((projectPreview) => projectPreview.ownerId).filter((ownerId) => ownerId !== null)),
	).toSorted((a, b) => {
		const aOrg = organizations.find((org) => org.id === a);
		const bOrg = organizations.find((org) => org.id === b);
		return (aOrg?.displayName ?? '').localeCompare(bOrg?.displayName ?? '');
	});

	const el = useDataElTagGenerator(WizardAdminProjectsPage);
	return (
		<ResizableLayout split='vertical' initialProportions={['18rem', '1fr']}>
			<ul {...el('projectRevisionList')} className='flex min-h-0 grow basis-0 flex-col gap-4 overflow-auto p-2'>
				{allOwnerIds.length === 0 && <li className='text-gray-a10 italic'>(No projects)</li>}

				{allOwnerIds.map((ownerId) => (
					<li {...el('projectRevisionListOrg')} key={ownerId} className='flex flex-col gap-3'>
						<span className='text-gray-a11'>{organizations.find((org) => org.id === ownerId)?.displayName}</span>
						<ul {...el('projectRevisionListOrgProjects')} className='relative flex flex-col gap-2'>
							<div className='absolute top-0 left-1 h-full w-px bg-gray-a7' />

							{projectPreviews
								.filter((projectPreview) => projectPreview.ownerId === ownerId)
								.toSorted((a, b) => a.name.localeCompare(b.name))
								.map((projectPreview) => (
									<li
										{...el('projectRevisionListOrgProject')}
										key={projectPreview.id}
										className='ml-4 flex flex-col gap-2'
									>
										<ProjectName isDemo={projectPreview.isDemo} className='text-gray-a11'>
											<span className='truncate'>{projectPreview.name}</span>
										</ProjectName>
										<ul {...el('projectRevisionListOrgProjectRevisions')} className='relative flex flex-col gap-0.5'>
											<div className='absolute top-0 left-1 h-full w-px bg-gray-a7' />

											{projectPreview.revisions.length === 0 && (
												<li className='ml-2 text-gray-a10 italic'>(No revisions)</li>
											)}
											{projectPreview.revisions.map((revision) => (
												<li {...el('projectRevisionListOrgProjectRevision')} key={revision.id} className='ml-4'>
													<Route.Link
														params={{ revisionId: revision.id }}
														className='-mx-1 block rounded px-1 py-1.5 font-medium hover:bg-gray-a3 data-[status=active]:bg-blue-a3'
													>
														→&nbsp;
														{revision.displayName ?? (
															<>
																Revision&nbsp;<span className='text-gray-a10'>{revision.id}</span>
															</>
														)}
													</Route.Link>
												</li>
											))}
										</ul>
									</li>
								))}
						</ul>
					</li>
				))}
			</ul>
			<div className='flex min-h-0 grow basis-0 flex-col'>
				<Suspense fallback={<Loading />}>
					{revisionId === undefined ?
						<div className='grid h-full w-full place-items-center p-2'>
							<p className='text-gray-a8 italic'>Select a revision to view details</p>
						</div>
					:	<WizardPlansForProject revisionId={revisionId} mpn={mpn} />}
				</Suspense>
			</div>
		</ResizableLayout>
	);
}
